"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.Substitute = exports.Rept = exports.Trim = exports.T = exports.Replace = exports.Mid = exports.Upper = exports.Lower = exports.Len = exports.Right = exports.Left = exports.EncodeUrlComponent = exports.Concatenate = exports.Search = exports.Find = void 0;
const basic_1 = require("./basic");
const types_1 = require("../../types");
const utils_1 = require("../../utils");
const lodash_1 = require("lodash");
const array_1 = require("./array");
const params_count_error_1 = require("../../formula_parser/errors/params_count.error");
class TextFunc extends basic_1.FormulaFunc {
}
TextFunc.type = types_1.FormulaFuncType.Text;
class Find extends TextFunc {
    static validateParams(params) {
        if (params.length < 2) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'FIND', 2);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Number;
    }
    static func(params) {
        var _a;
        const stringToFind = params[0].value;
        const whereToSearch = params[1].value;
        if (stringToFind == null || whereToSearch == null) {
            return 0;
        }
        if (!params[2] || params[2].value >= 0) {
            const startFromPosition = ((_a = params[2]) === null || _a === void 0 ? void 0 : _a.value) > 0 ? params[2].value - 1 : 0;
            return whereToSearch.indexOf(stringToFind, startFromPosition) + 1;
        }
        // positionIndex supports negative numbers, if a negative number is filled in, the position is calculated from the back to the front
        const startFromPosition = whereToSearch.length + params[2].value;
        return whereToSearch.lastIndexOf(stringToFind, startFromPosition) + 1;
    }
}
exports.Find = Find;
class Search extends TextFunc {
    static validateParams(params) {
        if (params.length < 2) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'SEARCH', 2);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Number;
    }
    static func(params) {
        const stringToFind = params[0].value;
        const whereToSearch = params[1].value;
        if (stringToFind == null || whereToSearch == null) {
            return null;
        }
        const startFromPosition = params[2] ? (params[2].value > 0 ? params[2].value - 1 : 0) : 0;
        const result = whereToSearch.indexOf(stringToFind, startFromPosition) + 1;
        if (result === 0) {
            return null;
        }
        return result;
    }
}
exports.Search = Search;
class Concatenate extends TextFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'CONCATENATE', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.String;
    }
    static func(params) {
        const flattenValue = (0, array_1.flattenParams)(params);
        return flattenValue.join('');
    }
}
exports.Concatenate = Concatenate;
Concatenate.acceptValueType = new Set([types_1.BasicValueType.Array, ...basic_1.FormulaFunc.acceptValueType]);
class EncodeUrlComponent extends TextFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'ENCODE_URL_COMPONENT', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.String;
    }
    static func(params) {
        const value = params[0].value;
        if (value == null) {
            return null;
        }
        return encodeURIComponent(String(value));
    }
}
exports.EncodeUrlComponent = EncodeUrlComponent;
class Left extends TextFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'LEFT', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.String;
    }
    static func(params) {
        const value = params[0].value;
        if (value == null) {
            return null;
        }
        const count = params[1] ? params[1].value : 1;
        return String(value).substring(0, count);
    }
}
exports.Left = Left;
class Right extends TextFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'RIGHT', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.String;
    }
    static func(params) {
        const value = params[0].value;
        if (value == null) {
            return null;
        }
        const valueStr = String(value);
        const count = params[1] ? params[1].value : 1;
        const startIndex = valueStr.length - count;
        return valueStr.substring(startIndex);
    }
}
exports.Right = Right;
class Len extends TextFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'LEN', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Number;
    }
    static func(params) {
        const value = params[0].value;
        if (value == null) {
            return null;
        }
        return String(value).length;
    }
}
exports.Len = Len;
class Lower extends TextFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'LOWER', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.String;
    }
    static func(params) {
        const value = params[0].value;
        if (value == null) {
            return null;
        }
        return String(value).toLowerCase();
    }
}
exports.Lower = Lower;
class Upper extends TextFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'UPPER', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.String;
    }
    static func(params) {
        const value = params[0].value;
        if (value == null) {
            return null;
        }
        return String(value).toUpperCase();
    }
}
exports.Upper = Upper;
class Mid extends TextFunc {
    static validateParams(params) {
        if (params.length < 3) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'MID', 3);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.String;
    }
    static func(params) {
        let [{ value: str }, { value: whereToStart }, { value: count }] = params;
        if (str == null) {
            return null;
        }
        str = String(str);
        whereToStart = Number(whereToStart) - 1;
        count = Number(count);
        return str.slice(whereToStart, whereToStart + count);
    }
}
exports.Mid = Mid;
class Replace extends TextFunc {
    static validateParams(params) {
        if (params.length < 4) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'REPLACE', 4);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.String;
    }
    static func(params) {
        let [{ value: str }, { value: whereToStart }, { value: count }, { value: replaceStr }] = params;
        if (str == null) {
            return null;
        }
        str = String(str);
        whereToStart = (0, utils_1.noNaN)(Number(whereToStart) - 1);
        count = (0, utils_1.noNaN)(Number(count));
        replaceStr = replaceStr == null ? '' : String(replaceStr);
        if (str.length <= whereToStart) {
            return str + replaceStr;
        }
        let ret = str.substring(0, whereToStart) + replaceStr;
        ret += str.substring(whereToStart + count);
        return ret;
    }
}
exports.Replace = Replace;
class T extends TextFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'T', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.String;
    }
    static func(params) {
        const str = params[0].value;
        if ((0, lodash_1.isString)(str)) {
            return str;
        }
        return null;
    }
}
exports.T = T;
T.acceptValueType = new Set([types_1.BasicValueType.DateTime, ...basic_1.FormulaFunc.acceptValueType]);
class Trim extends TextFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'TRIM', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.String;
    }
    static func(params) {
        const str = params[0].value;
        if (str == null) {
            return null;
        }
        return String(str).trim();
    }
}
exports.Trim = Trim;
Trim.acceptValueType = new Set([types_1.BasicValueType.Boolean, types_1.BasicValueType.String]);
class Rept extends TextFunc {
    static validateParams(params) {
        if (params.length < 2) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'REPT', 2);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.String;
    }
    static func(params) {
        let [{ value: str }, { value: count }] = params;
        if (str == null) {
            return null;
        }
        str = String(str);
        count = (0, utils_1.noNaN)(Number(count));
        return str.repeat(count);
    }
}
exports.Rept = Rept;
Rept.acceptValueType = new Set([types_1.BasicValueType.Boolean, types_1.BasicValueType.String]);
class Substitute extends TextFunc {
    static validateParams(params) {
        if (params.length < 3) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'SUBSTITUTE', 3);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.String;
    }
    static func(params) {
        const [{ value: str }, { value: oldText }, { value: newText }] = params;
        const index = params[3] && (0, utils_1.noNaN)(Number(params[3].value) - 1);
        const splitArr = String(str).split(oldText);
        if (str == null) {
            return null;
        }
        if (index < 1 || index > splitArr.length - 2) { // When the starting position does not match the range value, return the original string directly
            return String(str);
        }
        if (index) {
            const substituter = [splitArr[index], splitArr[index + 1]].join(newText);
            splitArr.splice(index, 2, substituter);
            return splitArr.join(oldText);
        }
        return splitArr.join(newText);
    }
}
exports.Substitute = Substitute;
Substitute.acceptValueType = new Set([types_1.BasicValueType.Boolean, types_1.BasicValueType.String]);
//# sourceMappingURL=text.js.map